import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Alert, Badge, Spinner, Modal, Form, Table } from "react-bootstrap";
import {
  Shield,
  CheckCircle,
  PersonPlus,
  PersonDash,
  Eye,
  TrashFill,
  ClockHistory,
  ExclamationTriangleFill,
  InfoCircleFill,
  Calendar,
  ClockFill,
  EyeSlash,
  Upload,
  ArrowCounterclockwise,
  Globe,
  LightningFill,
  CalculatorFill
} from 'react-bootstrap-icons';
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import SchoolRollNumberInput from "../components/SchoolRollNumberInput";
import ClassScreenerAssignment from "../components/ClassScreenerAssignment";
import ClassScreenerRemoval from "../components/ClassScreenerRemoval";
import ScreeningAccessManagement from "../components/ScreeningAccessManagement";
import CalculateStandardisedScoresModal from "../components/CalculateStandardisedScoresModal";
import CalculateStandardisedScoresStepModal from "../components/CalculateStandardisedScoresStepModal";
import "../components/SchoolAdmin.css";
import {
  whoami,
  getSchools,
  cleanupOrphanedInstances,
  cleanupIncompleteInstances,
  getAssessmentTypes,
  previewBulkAssignScreener,
  bulkAssignScreener,
  getScreeningWindowStatuses,
  createScreeningWindowStatus,
  upsertScreeningWindowStatus,
  updateScreeningWindowStatus,
  toggleScreeningWindowActive,
  deleteScreeningWindowStatus,
  getCountries,
  getTagDefinitions,
  getEntityTags,
  updateEntityTag,
  getAssessmentStatisticsContext
} from "../services/APIClient";
import { isTeacher } from "../utils/auth";
import { FaChalkboardTeacher, FaSchool } from "react-icons/fa";

function ScreenerManagement() {
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );
  const [name, setName] = useState("User");
  const [teacherId, setTeacherId] = useState(null);
  const [schoolid, setSchoolid] = useState("");
  const [schoolName, setSchoolName] = useState("Loading school information...");
  const [rollnumber, setRollnumber] = useState("");
  const [isLoadingSchool, setIsLoadingSchool] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // ✅ Check if current user is a teacher
  const teacherMode = isTeacher();

  // Loading and messaging states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal states
  const [showAccessManagementModal, setShowAccessManagementModal] = useState(false);
  const [showClassAssignmentView, setShowClassAssignmentView] = useState(false);
  const [showClassRemovalView, setShowClassRemovalView] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);

  // Cleanup states
  const [cleanupStartDate, setCleanupStartDate] = useState('');
  const [cleanupEndDate, setCleanupEndDate] = useState('');
  const [availableAssessments, setAvailableAssessments] = useState([]);
  const [selectedAssessments, setSelectedAssessments] = useState(new Set());
  const [cleanupResults, setCleanupResults] = useState(null);
  const [orphanedInstances, setOrphanedInstances] = useState([]);
  const [incompleteInstances, setIncompleteInstances] = useState([]);
  const [cleanupStep, setCleanupStep] = useState('form');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [showNonStandard, setShowNonStandard] = useState(false);

  // Bulk Assign states
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignStep, setBulkAssignStep] = useState('form');
  const [bulkAssignCountry, setBulkAssignCountry] = useState('');
  const [bulkAssignAvailableAssessments, setBulkAssignAvailableAssessments] = useState([]);
  const [bulkAssignSelectedAssessments, setBulkAssignSelectedAssessments] = useState(new Set());
  const [bulkAssignPreviewResults, setBulkAssignPreviewResults] = useState([]);
  const [bulkAssignResults, setBulkAssignResults] = useState([]);
  const [bulkAssignShowNonStandard, setBulkAssignShowNonStandard] = useState(false);

  // Screening Window states
  const [showScreeningWindowModal, setShowScreeningWindowModal] = useState(false);
  const [screeningWindows, setScreeningWindows] = useState([]);
  const [editingWindow, setEditingWindow] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [windowFormData, setWindowFormData] = useState({
    countryid: '',
    academic_year: '',
    start_date: '',
    end_date: '',
    screening_window: 'Baseline',
    message: '',
    active: true,
    created_by: teacherId,
    updated_by: teacherId
  });

  // Standardised Score Management states
  const [showStandardisedScoreModal, setShowStandardisedScoreModal] = useState(false);
  const [showCalculateStandardisedScoresModal, setShowCalculateStandardisedScoresModal] = useState(false);
  const [showCalculateStandardisedScoresStepModal, setShowCalculateStandardisedScoresStepModal] = useState(false);
  const [assessmentsWithTags, setAssessmentsWithTags] = useState([]);
  const [standardisedScoreTagDefId, setStandardisedScoreTagDefId] = useState(null);
  const [loadingStandardisedScores, setLoadingStandardisedScores] = useState(false);

  // Country Standardised Score Management states
  const [showCountryStandardisedScoreModal, setShowCountryStandardisedScoreModal] = useState(false);
  const [countriesWithTags, setCountriesWithTags] = useState([]);
  const [countryStandardisedScoreTagDefId, setCountryStandardisedScoreTagDefId] = useState(null);
  const [loadingCountryStandardisedScores, setLoadingCountryStandardisedScores] = useState(false);

  // Check if school has been found
  const isSchoolFound = schoolid && schoolid !== "" && schoolName !== "Loading school information..." && schoolName !== "Please enter school roll number and press 'Find School'";

  // Handle device orientation
  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };
    window.addEventListener("resize", handleOrientationChange);
    return () => window.removeEventListener("resize", handleOrientationChange);
  }, []);

  // ✅ Fetch user and school information based on role
  useEffect(() => {
    const fetchUserAndSchoolInfo = async () => {
      try {
        const response = await whoami();
        const stakeholder = response.data.stakeholder;
        setTeacherId(stakeholder.id);
        setName(stakeholder.name);

        // ✅ TEACHER MODE: Auto-load their school
        if (teacherMode) {
          const teacherSchoolId = stakeholder.schoolid || stakeholder.schoolId;

          if (!teacherSchoolId) {
            console.error("❌ Teacher has no school ID!");
            setSchoolName("Error: No school assigned");
            setLoadError("No school assigned to your account");
            setIsLoadingSchool(false);
            return;
          }

          console.log("🏫 Teacher mode: Auto-loading school ID:", teacherSchoolId);
          setSchoolid(teacherSchoolId);

          // Fetch school details
          try {
            const response = await getSchools();
            const schools = response.data || response;
            const teacherSchool = Array.isArray(schools)
              ? schools.find(s => s.id === teacherSchoolId)
              : null;

            if (teacherSchool) {
              setSchoolName(teacherSchool.name);
              setRollnumber(teacherSchool.rollnumber || "");
              console.log("✅ Loaded teacher's school:", teacherSchool.name);
            } else {
              setSchoolName("School not found");
              setLoadError("School not found in database");
            }
          } catch (error) {
            console.error("❌ Error fetching school details:", error);
            setSchoolName("Error loading school");
            setLoadError(`Error loading school: ${error.message}`);
          }

          setIsLoadingSchool(false);
        } else {
          // ✅ ALPACA-STAFF MODE: Show search interface
          setSchoolName("Please enter school roll number and press 'Find School'");
          setIsLoadingSchool(false);
        }
      } catch (error) {
        console.error("❌ Error in whoami:", error);
        setSchoolName("Error loading user information");
        setLoadError(`Error loading user: ${error.message}`);
        setIsLoadingSchool(false);
      }
    };

    fetchUserAndSchoolInfo();
  }, [teacherMode]);

  // Clear messages when school changes
  useEffect(() => {
    setError(null);
    setSuccess(null);
    setShowClassAssignmentView(false);
    setShowClassRemovalView(false);
  }, [schoolid]);

  // Reset cleanup modal when closed
  const handleCloseCleanupModal = () => {
    setShowCleanupModal(false);
    setCleanupStep('form');
    setCleanupResults(null);
    setOrphanedInstances([]);
    setIncompleteInstances([]);
    setSelectedAssessments(new Set());
    setAvailableAssessments([]);
    setSelectedCountry('');
    setShowNonStandard(false);
    setError(null);
    setSuccess(null);
  };

  // Load available assessments when cleanup modal opens
  const handleOpenCleanupModal = async () => {
    setShowCleanupModal(true);
    setLoading(true);
    setError(null);

    try {
      const [assessmentResponse] = await Promise.all([
        getAssessmentTypes(),
        availableCountries.length === 0 ? loadCountries() : Promise.resolve()
      ]);
      const filteredAssessments = assessmentResponse.filter(assessment => assessment.task !== true && assessment.rti !== true);

      setAvailableAssessments(filteredAssessments);
    } catch (err) {
      setError(`Failed to load assessments: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle assessment selection
  const handleAssessmentToggle = (assessmentId) => {
    setSelectedAssessments(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(assessmentId)) {
        newSelection.delete(assessmentId);
      } else {
        newSelection.add(assessmentId);
      }
      return newSelection;
    });
  };

  const getFilteredAssessments = () => {
    if (!selectedCountry) return availableAssessments;
    return availableAssessments.filter(a => a.countryid === selectedCountry);
  };

  const PERIOD_ORDER = ['Baseline', 'Midpoint', 'End of Year'];
  const getPeriodOrder = (name) => {
    const idx = PERIOD_ORDER.findIndex(p => name.includes(p));
    return idx === -1 ? 99 : idx;
  };

  const handleSelectAllAssessments = () => {
    const visible = getFilteredAssessments();
    if (selectedAssessments.size === visible.length) {
      setSelectedAssessments(new Set());
    } else {
      setSelectedAssessments(new Set(visible.map(a => a.id)));
    }
  };

  // Bulk Assign handlers
  const handleOpenBulkAssignModal = async () => {
    setShowBulkAssignModal(true);
    setLoading(true);
    setError(null);
    try {
      const [response] = await Promise.all([
        getAssessmentTypes(),
        availableCountries.length === 0 ? loadCountries() : Promise.resolve()
      ]);
      setBulkAssignAvailableAssessments(response.filter(a => a.task !== true && a.rti !== true));
    } catch (err) {
      setError(`Failed to load assessments: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseBulkAssignModal = () => {
    setShowBulkAssignModal(false);
    setBulkAssignStep('form');
    setBulkAssignCountry('');
    setBulkAssignSelectedAssessments(new Set());
    setBulkAssignAvailableAssessments([]);
    setBulkAssignPreviewResults([]);
    setBulkAssignResults([]);
    setBulkAssignShowNonStandard(false);
    setError(null);
    setSuccess(null);
  };

  const getBulkFilteredAssessments = () => {
    if (!bulkAssignCountry) return bulkAssignAvailableAssessments;
    return bulkAssignAvailableAssessments.filter(a => a.countryid === bulkAssignCountry);
  };

  const handleBulkAssignToggle = (id) => {
    setBulkAssignSelectedAssessments(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkAssignPreview = async () => {
    setBulkAssignStep('previewing');
    setError(null);
    try {
      const assessmentIds = Array.from(bulkAssignSelectedAssessments);
      const results = await Promise.all(
        assessmentIds.map(async (id) => {
          const assessment = bulkAssignAvailableAssessments.find(a => a.id === id);
          const result = await previewBulkAssignScreener(bulkAssignCountry, id);
          return { assessmentId: id, assessmentName: assessment?.name || id, ...result };
        })
      );
      setBulkAssignPreviewResults(results);
      setBulkAssignStep('preview');
    } catch (err) {
      setError(`Preview failed: ${err.message}`);
      setBulkAssignStep('form');
    }
  };

  const handleBulkAssignExecute = async () => {
    setBulkAssignStep('executing');
    setError(null);
    const initialResults = bulkAssignPreviewResults.map(p => ({ ...p, status: 'pending' }));
    setBulkAssignResults(initialResults);

    const live = [...initialResults];
    for (let i = 0; i < live.length; i++) {
      live[i] = { ...live[i], status: 'executing' };
      setBulkAssignResults([...live]);
      try {
        const result = await bulkAssignScreener(bulkAssignCountry, live[i].assessmentId);
        live[i] = { ...live[i], status: 'done', ...result };
      } catch (err) {
        live[i] = { ...live[i], status: 'error', errorMessage: err.message };
      }
      setBulkAssignResults([...live]);
    }
    setBulkAssignStep('completed');
    setSuccess('Bulk assignment completed.');
  };

  // Handle dry run to preview what will be cleaned
  const handleDryRun = async () => {
    if (!cleanupStartDate || !cleanupEndDate || selectedAssessments.size === 0) {
      setError("Please fill in all required fields and select at least one assessment");
      return;
    }

    if (!teacherId) {
      setError("Teacher ID not available. Please refresh the page and try again.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const assessmentIds = Array.from(selectedAssessments);

      const [orphanedResult, incompleteResult] = await Promise.all([
        cleanupOrphanedInstances(cleanupStartDate, cleanupEndDate, assessmentIds, teacherId, true),
        cleanupIncompleteInstances(cleanupStartDate, cleanupEndDate, assessmentIds, teacherId, true)
      ]);

      setOrphanedInstances(orphanedResult.instances || []);
      setIncompleteInstances(incompleteResult.instances || []);

      setCleanupResults({
        orphanedFound: orphanedResult.found,
        incompleteFound: incompleteResult.found
      });

      setCleanupStep('preview');
    } catch (err) {
      setError(`Failed to preview cleanup: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteCleanup = async () => {
    setLoading(true);
    setCleanupStep('executing');
    setError(null);

    try {
      const assessmentIds = Array.from(selectedAssessments);

      const [orphanedResult, incompleteResult] = await Promise.all([
        cleanupResults.orphanedFound > 0
          ? cleanupOrphanedInstances(cleanupStartDate, cleanupEndDate, assessmentIds, teacherId, false)
          : { deleted: 0 },
        cleanupResults.incompleteFound > 0
          ? cleanupIncompleteInstances(cleanupStartDate, cleanupEndDate, assessmentIds, teacherId, false)
          : { completed: 0 }
      ]);

      setCleanupResults({
        ...cleanupResults,
        orphanedDeleted: orphanedResult.deleted || 0,
        incompleteCompleted: incompleteResult.completed || 0
      });

      setCleanupStep('completed');
      setSuccess('Cleanup completed successfully!');
    } catch (err) {
      setError(`Failed to execute cleanup: ${err.message}`);
      setCleanupStep('preview');
    } finally {
      setLoading(false);
    }
  };

  // Screening Window Management functions
  const handleOpenScreeningWindowModal = async () => {
    setShowScreeningWindowModal(true);
    await loadScreeningWindows();
    await loadCountries();
  };

  const handleCloseScreeningWindowModal = () => {
    setShowScreeningWindowModal(false);
    setEditingWindow(null);
    resetWindowForm();
  };

  const loadScreeningWindows = async () => {
    setLoading(true);
    try {
      const windows = await getScreeningWindowStatuses();
      setScreeningWindows(windows || []);
    } catch (err) {
      setError(`Failed to load screening windows: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async () => {
    try {
      const countries = await getCountries();
      setAvailableCountries(countries || []);
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  };

  const resetWindowForm = () => {
    setWindowFormData({
      countryid: '',
      academic_year: '',
      start_date: '',
      end_date: '',
      screening_window: 'Baseline',
      message: '',
      active: true,
      created_by: teacherId,
      updated_by: teacherId
    });
  };

  const handleWindowFormChange = (field, value) => {
    setWindowFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveWindow = async () => {
    if (!windowFormData.countryid || !windowFormData.academic_year ||
      !windowFormData.start_date || !windowFormData.end_date) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingWindow) {
        await updateScreeningWindowStatus(editingWindow.id, {
          ...windowFormData,
          updated_by: teacherId
        });
        setSuccess('Screening window updated successfully');
      } else {
        await createScreeningWindowStatus({
          ...windowFormData,
          created_by: teacherId,
          updated_by: teacherId
        });
        setSuccess('Screening window created successfully');
      }

      await loadScreeningWindows();
      setEditingWindow(null);
      resetWindowForm();
    } catch (err) {
      setError(`Failed to save screening window: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditWindow = (window) => {
    setEditingWindow(window);
    setWindowFormData({
      countryid: window.countryid,
      academic_year: window.academic_year,
      start_date: window.start_date?.split('T')[0] || '',
      end_date: window.end_date?.split('T')[0] || '',
      screening_window: window.screening_window,
      message: window.message || '',
      active: window.active,
      created_by: window.created_by,
      updated_by: teacherId
    });
  };

  const handleToggleWindowActive = async (windowId, currentActive) => {
    setLoading(true);
    try {
      await toggleScreeningWindowActive(windowId);
      await loadScreeningWindows();
      setSuccess(`Window ${currentActive ? 'deactivated' : 'activated'} successfully`);
    } catch (err) {
      setError(`Failed to toggle window: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWindow = async (windowId) => {
    if (!window.confirm('Are you sure you want to delete this screening window?')) {
      return;
    }

    setLoading(true);
    try {
      await deleteScreeningWindowStatus(windowId);
      await loadScreeningWindows();
      setSuccess('Screening window deleted successfully');
    } catch (err) {
      setError(`Failed to delete window: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatIrishDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateRange = (startDate, endDate) => {
    return `${formatIrishDate(startDate)} - ${formatIrishDate(endDate)}`;
  };

  // ✅ Global functions - Staff only, no school selection required
  const globalFunctions = [
    {
      category: "Global System Configuration",
      description: "Manage system-wide screening settings",
      items: [
        {
          name: "Screening Access Management",
          key: "accessManagement",
          icon: Shield,
          description: "Control which screening assessments are available globally",
          color: "#3b82f6",
          action: () => setShowAccessManagementModal(true)
        },
        {
          name: "Screening Window Management",
          key: "windowManagement",
          icon: Calendar,
          description: "Define time windows when screening assessments are available",
          color: "#06b6d4",
          action: () => handleOpenScreeningWindowModal()
        },
        {
          name: "Standardised Score Controls",
          key: "standardisedScoreControls",
          icon: CheckCircle, // or use a different icon
          description: "Enable/disable standardised scores for screening windows",
          color: "#8b5cf6",
          action: () => handleOpenStandardisedScoreModal()
        },
        {
          name: "Calculate Standardised Scores",
          key: "calculateStandardisedScores",
          icon: CalculatorFill,
          description: "Check answer counts and manually calculate standardised scores",
          color: "#7c3aed",
          action: () => setShowCalculateStandardisedScoresModal(true)
        },
        {
          name: "Calculate Standardised Scores (Step-by-step)",
          key: "calculateStandardisedScoresStepByStep",
          icon: CalculatorFill,
          description: "Calculate TaskStatistics and run Normalised Score recalculation one task at a time",
          color: "#7c3aed",
          action: () => setShowCalculateStandardisedScoresStepModal(true)
        },
        {
          name: "Country Standardised Score Controls",
          key: "countryStandardisedScoreControls",
          icon: Globe,
          description: "Enable/disable standardised scores at country level",
          color: "#10b981",
          action: () => handleOpenCountryStandardisedScoreModal()
        },
        {
          name: "Assessment Cleanup",
          key: "cleanup",
          icon: TrashFill,
          description: "Clean up orphaned assessments and force-complete instances",
          color: "#f59e0b",
          action: () => handleOpenCleanupModal()
        },
        {
          name: "Bulk Assign Screeners",
          key: "bulkAssign",
          icon: LightningFill,
          description: "Bulk assign screener assessments to all eligible students in a country",
          color: "#6366f1",
          action: () => handleOpenBulkAssignModal()
        }
      ]
    }
  ];

  // ✅ School-specific functions - Require school selection
  const schoolFunctions = [
    {
      category: "Managing Screeners",
      description: "Assign and remove screeners for classes in your school",
      items: [
        {
          name: "Assign Screeners to Classes",
          key: "assignScreeners",
          icon: PersonPlus,
          description: "Assign individual screeners to classes and students",
          color: "#10b981",
          action: () => setShowClassAssignmentView(true)
        },
        {
          name: "Remove Screeners from Classes",
          key: "removeScreeners",
          icon: PersonDash,
          description: "Remove screener assignments from classes and students",
          color: "#ef4444",
          action: () => setShowClassRemovalView(true)
        }
      ]
    },
    {
      category: "Data Management",
      description: "Export data and manage task assignments",
      items: [
        {
          name: "Aladdin Export",
          key: "aladdinexport",
          icon: Upload,
          description: "Export data to Aladdin system",
          color: "#6366f1",
          action: () => window.location.href = "/aladdinexport"
        },
        {
          name: "Resetting Tasks",
          key: "resettasks",
          icon: ArrowCounterclockwise,
          description: "Reset and manage task assignments",
          color: "#f59e0b",
          action: () => window.location.href = "/resettasks"
        }
      ]
    }
  ];

  // Standardised Score Management functions
  const handleOpenStandardisedScoreModal = async () => {
    setShowStandardisedScoreModal(true);
    await loadAssessmentsWithStandardisedScoreTags();
  };

  const handleCloseStandardisedScoreModal = () => {
    setShowStandardisedScoreModal(false);
    setError(null);
    setSuccess(null);
  };

  const loadAssessmentsWithStandardisedScoreTags = async () => {
    setLoadingStandardisedScores(true);
    setError(null);

    try {
      // 1. Get the standardised_scores_enabled tag definition
      const tagDefs = await getTagDefinitions();
      const standardisedScoreDef = tagDefs.find(
        def => def.tag_name === 'standardised_scores_enabled'
      );

      if (!standardisedScoreDef) {
        setError('Standardised score tag definition not found. Please create it first.');
        setLoadingStandardisedScores(false);
        return;
      }

      setStandardisedScoreTagDefId(standardisedScoreDef.id);

      // 2. Get all assessments
      const response = await getAssessmentTypes();
      const assessments = response.filter(assessment => assessment.task !== true);

      // 3. For each assessment, check if it has the standardised_scores_enabled tag,
      // and (additive) how complete its TaskStatistics/Normalised Score calculation
      // is, so staff can see that before enabling reporting for it.
      const enrichedAssessments = await Promise.all(
        assessments.map(async (assessment) => {
          const completeness = await getAssessmentStatisticsContext(
            assessment.id,
            assessment.countryid,
          )
            .then((context) => {
              const tasks = context?.tasks || [];
              return {
                calculatedTaskCount: tasks.filter((task) => task.taskStatistics).length,
                totalTaskCount: tasks.length,
              };
            })
            .catch(() => ({ calculatedTaskCount: null, totalTaskCount: null }));

          try {
            const tags = await getEntityTags('assessment', assessment.id);
            const standardisedScoreTag = tags.find(
              tag => tag.tag_definition_id === standardisedScoreDef.id
            );

            return {
              ...assessment,
              standardisedScoresEnabled: standardisedScoreTag?.tag_value === 'ON',
              hasTag: !!standardisedScoreTag,
              ...completeness
            };
          } catch (err) {
            console.error(`Error loading tags for assessment ${assessment.id}:`, err);
            return {
              ...assessment,
              standardisedScoresEnabled: false,
              hasTag: false,
              ...completeness
            };
          }
        })
      );

      // Group by screening window (assuming assessments have a property for this)
      // You may need to adjust this based on your actual data structure
      const grouped = enrichedAssessments.reduce((acc, assessment) => {
        // Try to determine screening window from assessment name
        let window = 'Other';
        if (assessment.name?.includes('Baseline')) window = 'Baseline';
        else if (assessment.name?.includes('Midpoint')) window = 'Midpoint';
        else if (assessment.name?.includes('End of Year')) window = 'End of Year';

        if (!acc[window]) acc[window] = [];
        acc[window].push(assessment);
        return acc;
      }, {});

      setAssessmentsWithTags(grouped);
    } catch (err) {
      setError(`Failed to load assessments: ${err.message}`);
    } finally {
      setLoadingStandardisedScores(false);
    }
  };

  const handleToggleStandardisedScore = async (assessmentId, currentValue, assessment) => {
    if (!standardisedScoreTagDefId) {
      setError('Tag definition not loaded. Please refresh and try again.');
      return;
    }

    if (!teacherId) {  // ADD THIS CHECK
      setError('User ID not available. Please refresh and try again.');
      return;
    }

    const turningOn = !currentValue;
    const isIncomplete =
      assessment &&
      assessment.totalTaskCount !== null &&
      (assessment.totalTaskCount === 0 || assessment.calculatedTaskCount !== assessment.totalTaskCount);
    if (turningOn && isIncomplete) {
      const confirmed = window.confirm(
        `Only ${assessment.calculatedTaskCount} of ${assessment.totalTaskCount} tasks have current standardised-score data for this assessment. Enable reporting anyway?`,
      );
      if (!confirmed) return;
    }

    setLoadingStandardisedScores(true);
    setError(null);
    setSuccess(null);

    try {
      const newValue = currentValue ? 'OFF' : 'ON';

      await updateEntityTag(
        'assessment',
        assessmentId,
        standardisedScoreTagDefId,
        newValue,
        teacherId  // ADD THIS PARAMETER
      );

      setSuccess(`Standardised scores ${newValue === 'ON' ? 'enabled' : 'disabled'} successfully`);

      // Reload the assessments
      await loadAssessmentsWithStandardisedScoreTags();
    } catch (err) {
      setError(`Failed to update standardised score setting: ${err.message}`);
    } finally {
      setLoadingStandardisedScores(false);
    }
  };

  // Country Standardised Score Management functions
  const handleOpenCountryStandardisedScoreModal = async () => {
    setShowCountryStandardisedScoreModal(true);
    await loadCountriesWithStandardisedScoreTags();
  };

  const handleCloseCountryStandardisedScoreModal = () => {
    setShowCountryStandardisedScoreModal(false);
    setError(null);
    setSuccess(null);
  };

  const loadCountriesWithStandardisedScoreTags = async () => {
    setLoadingCountryStandardisedScores(true);
    setError(null);

    try {
      // Get the country_standardised_scores_enabled tag definition
      const tagDefs = await getTagDefinitions();
      const standardisedScoreDef = tagDefs.find(
        def => def.tag_name === 'country_standardised_scores_enabled'
      );

      if (!standardisedScoreDef) {
        setError('Country standardised score tag definition not found. Please create it first.');
        setLoadingCountryStandardisedScores(false);
        return;
      }

      setCountryStandardisedScoreTagDefId(standardisedScoreDef.id);

      // Get all countries
      const countries = await getCountries();

      // For each country, check if it has the country_standardised_scores_enabled tag
      const enrichedCountries = await Promise.all(
        countries.map(async (country) => {
          try {
            const tags = await getEntityTags('country', country.id);
            const standardisedScoreTag = tags.find(
              tag => tag.tag_definition_id === standardisedScoreDef.id
            );

            return {
              ...country,
              standardisedScoresEnabled: standardisedScoreTag?.tag_value === 'ON',
              hasTag: !!standardisedScoreTag
            };
          } catch (err) {
            console.error(`Error loading tags for country ${country.id}:`, err);
            return {
              ...country,
              standardisedScoresEnabled: false,
              hasTag: false
            };
          }
        })
      );

      setCountriesWithTags(enrichedCountries);
    } catch (err) {
      setError(`Failed to load countries: ${err.message}`);
    } finally {
      setLoadingCountryStandardisedScores(false);
    }
  };

  const handleToggleCountryStandardisedScore = async (countryId, currentValue) => {
    if (!countryStandardisedScoreTagDefId) {
      setError('Tag definition not loaded. Please refresh and try again.');
      return;
    }

    if (!teacherId) {
      setError('User ID not available. Please refresh and try again.');
      return;
    }

    setLoadingCountryStandardisedScores(true);
    setError(null);
    setSuccess(null);

    try {
      const newValue = currentValue ? 'OFF' : 'ON';

      await updateEntityTag(
        'country',
        countryId,
        countryStandardisedScoreTagDefId,
        newValue,
        teacherId
      );

      setSuccess(`Standardised scores ${newValue === 'ON' ? 'enabled' : 'disabled'} for country`);

      // Reload countries
      await loadCountriesWithStandardisedScoreTags();
    } catch (err) {
      setError(`Failed to update country standardised score setting: ${err.message}`);
    } finally {
      setLoadingCountryStandardisedScores(false);
    }
  };

  const AdminFunctionCard = ({ item }) => (
    <Card
      className="class-admin-card"
      onClick={item.action}
      style={{ cursor: 'pointer' }}
    >
      <Card.Body className="class-admin-card-body">
        <div className="class-admin-icon" style={{ backgroundColor: `${item.color}15`, color: item.color }}>
          <item.icon size={20} />
        </div>
        <div className="class-admin-content">
          <h6 className="class-admin-title">{item.name}</h6>
          <p className="class-admin-description">{item.description}</p>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <div>
      <Container fluid className="class-admin-container">
        <HeadingBanner name={`Welcome ${name}!`} isLoggedIn={true} cloud={true} />

        <Row className="class-admin-row">
          <LeftPanel />

          <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">

            {/* Page Header */}
            <div className="class-admin-header">
              <h2 className="class-admin-page-title">Screener Management</h2>
              <p className="class-admin-page-subtitle">
                {teacherMode
                  ? "Manage screener assignments for your school"
                  : "Control screening access globally, manage screener assignments, and clean up assessment data"
                }
              </p>
            </div>

            {/* ✅ ERROR DISPLAY */}
            {loadError && (
              <Alert variant="danger" className="mb-4">
                <strong>⚠️ Error Loading School</strong>
                <p className="mb-0">{loadError}</p>
              </Alert>
            )}

            {/* Error/Success messages */}
            {error && (
              <Alert variant="danger" className="mb-4">
                <strong>Error: </strong>{error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" className="mb-4">
                <strong>Success: </strong>{success}
              </Alert>
            )}

            {/* ✅ GLOBAL FUNCTIONS - Staff only, always visible */}
            {!teacherMode && (
              <div className="admin-functions-container mb-4">
                {globalFunctions.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="function-category">
                    <h4 className="category-title">{category.category}</h4>
                    {category.description && (
                      <p className="category-description text-muted">{category.description}</p>
                    )}
                    <div className="function-cards-grid">
                      {category.items.map((item, itemIndex) => (
                        <AdminFunctionCard key={itemIndex} item={item} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ✅ SCHOOL SELECTOR */}
            <div className="school-selector-section">
              {teacherMode ? (
                // ✅ TEACHER VIEW: Show their school (locked)
                <div className="selected-school locked-school">
                  <Alert variant="secondary" className="mb-3">
                    <FaChalkboardTeacher className="me-2" />
                    You are managing screeners for the following school:
                  </Alert>
                  {isLoadingSchool ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading your school information...</p>
                    </div>
                  ) : (
                    <>
                      <h3 className="school-name">{schoolName}</h3>
                      {rollnumber && (
                        <p className="text-muted">Roll Number: {rollnumber}</p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                // ✅ ALPACA-STAFF VIEW: Show search interface
                <>
                  <SchoolRollNumberInput
                    onSchoolIdRetrieved={setSchoolid}
                    schoolNameRetrieved={(name, rollNum) => {
                      setSchoolName(name);
                      if (rollNum) setRollnumber(rollNum);
                    }}
                  />
                  <div className="selected-school">
                    <h3 className="school-name">{schoolName}</h3>
                    {rollnumber && (
                      <p className="text-muted">Roll Number: {rollnumber}</p>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* ✅ SCHOOL-SPECIFIC FUNCTIONS - Only show when school selected */}
            {isSchoolFound && (
              <div className="admin-functions-container">
                {schoolFunctions.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="function-category">
                    <h4 className="category-title">{category.category}</h4>
                    {category.description && (
                      <p className="category-description text-muted">{category.description}</p>
                    )}
                    <div className="function-cards-grid">
                      {category.items.map((item, itemIndex) => (
                        <AdminFunctionCard key={itemIndex} item={item} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Show message when no school selected */}
            {!isSchoolFound && !isLoadingSchool && (
              <div className="no-school-message">
                {teacherMode
                  ? loadError || "Unable to load school information. Please contact support."
                  : "Please select a school to access screener assignment functions."
                }
              </div>
            )}

          </Col>
        </Row>
      </Container>

      {/* Access Management Modal */}
      <Modal
        show={showAccessManagementModal}
        onHide={() => setShowAccessManagementModal(false)}
        size="xl"
        fullscreen
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Shield className="me-2" />
            Screening Access Management
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <ScreeningAccessManagement
            onClose={() => setShowAccessManagementModal(false)}
          />
        </Modal.Body>
      </Modal>

      {/* Assessment Cleanup Modal - Staff Only */}
      {!teacherMode && (
        <Modal
          show={showCleanupModal}
          onHide={handleCloseCleanupModal}
          size="xl"
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <TrashFill className="me-2" />
              Assessment Data Cleanup
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Alert variant="warning">
              <ExclamationTriangleFill className="me-2" />
              <strong>Warning:</strong> This operation will permanently delete or modify assessment data.
              Always preview changes before executing.
            </Alert>

            {cleanupStep === 'form' && (
              <div>
                <Form>
                  <Row className="mb-4">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Country</Form.Label>
                        <Form.Select
                          value={selectedCountry}
                          onChange={(e) => setSelectedCountry(e.target.value)}
                        >
                          <option value="">All Countries</option>
                          {availableCountries.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={cleanupStartDate}
                          onChange={(e) => setCleanupStartDate(e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>End Date</Form.Label>
                        <Form.Control
                          type="date"
                          value={cleanupEndDate}
                          onChange={(e) => setCleanupEndDate(e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Select Assessments to Clean</h6>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleSelectAllAssessments}
                      >
                        {selectedAssessments.size === getFilteredAssessments().length ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>

                    {loading ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading assessments...</p>
                      </div>
                    ) : (() => {
                      const filtered = getFilteredAssessments();
                      const standardAssessments = filtered.filter(a => a.standard === true);
                      const nonStandardAssessments = filtered.filter(a => a.standard !== true);

                      const sortByPeriod = (arr) => [...arr].sort((a, b) => getPeriodOrder(a.name) - getPeriodOrder(b.name));

                      const grade1 = sortByPeriod(standardAssessments.filter(a => a.grade_level1 === true));
                      const grade2 = sortByPeriod(standardAssessments.filter(a => a.grade_level2 === true));

                      const renderCheckboxes = (assessments) => (
                        <div className="d-flex flex-wrap gap-2">
                          {assessments.map(assessment => (
                            <Form.Check
                              key={assessment.id}
                              type="checkbox"
                              id={`assessment-${assessment.id}`}
                              label={assessment.name}
                              checked={selectedAssessments.has(assessment.id)}
                              onChange={() => handleAssessmentToggle(assessment.id)}
                              className="border rounded p-2"
                            />
                          ))}
                        </div>
                      );

                      return (
                        <>
                          {grade1.length > 0 && (
                            <div className="mb-3">
                              <h6 className="text-muted mb-2">Grade 1</h6>
                              {renderCheckboxes(grade1)}
                            </div>
                          )}
                          {grade2.length > 0 && (
                            <div className="mb-3">
                              <h6 className="text-muted mb-2">Grade 2</h6>
                              {renderCheckboxes(grade2)}
                            </div>
                          )}
                          {nonStandardAssessments.length > 0 && (
                            <div className="mb-3">
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-muted mb-2"
                                onClick={() => setShowNonStandard(prev => !prev)}
                              >
                                {showNonStandard ? '▾' : '▸'} Non-standard ({nonStandardAssessments.length})
                              </Button>
                              {showNonStandard && renderCheckboxes(sortByPeriod(nonStandardAssessments))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <div className="d-flex justify-content-between">
                    <Button
                      variant="outline-secondary"
                      onClick={handleCloseCleanupModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleDryRun}
                      disabled={loading || !cleanupStartDate || !cleanupEndDate || selectedAssessments.size === 0}
                    >
                      {loading ? <Spinner animation="border" size="sm" className="me-2" /> : <Eye className="me-2" />}
                      Preview Changes
                    </Button>
                  </div>
                </Form>
              </div>
            )}

            {cleanupStep === 'preview' && cleanupResults && (
              <div>
                <Alert variant="info">
                  <InfoCircleFill className="me-2" />
                  <strong>Preview:</strong> Review the changes below before executing the cleanup.
                </Alert>

                <Row className="mb-4">
                  <Col md={6}>
                    <Card className="border-danger">
                      <Card.Header className="bg-danger text-white">
                        <h6 className="mb-0">Orphaned Instances Found</h6>
                      </Card.Header>
                      <Card.Body>
                        <h4 className="text-danger">{cleanupResults.orphanedFound}</h4>
                        <p className="text-muted mb-0">Will be permanently deleted</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="border-warning">
                      <Card.Header className="bg-warning text-dark">
                        <h6 className="mb-0">Incomplete with Answers</h6>
                      </Card.Header>
                      <Card.Body>
                        <h4 className="text-warning">{cleanupResults.incompleteFound}</h4>
                        <p className="text-muted mb-0">Will be force-completed</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Selected Assessments Summary */}
                <div className="mb-4">
                  <h6>Selected Assessments ({selectedAssessments.size}):</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {Array.from(selectedAssessments).map(assessmentId => {
                      const assessment = availableAssessments.find(a => a.id === assessmentId);
                      return assessment ? (
                        <Badge key={assessmentId} bg="primary" className="p-2">
                          {assessment.name}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>

                {orphanedInstances.length > 0 && (
                  <div className="mb-4">
                    <h6>Orphaned Instances to be Deleted:</h6>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <Table striped size="sm">
                        <thead>
                          <tr>
                            <th>Instance ID</th>
                            <th>Student ID</th>
                            <th>Assessment ID</th>
                            <th>Date Assigned</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orphanedInstances.slice(0, 50).map((instance) => (
                            <tr key={instance.id}>
                              <td className="font-monospace small">{instance.id}</td>
                              <td>{instance.studentid}</td>
                              <td className="font-monospace small">{instance.assessmentid}</td>
                              <td>{new Date(instance.date).toLocaleDateString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      {orphanedInstances.length > 50 && (
                        <p className="text-muted small">Showing first 50 of {orphanedInstances.length} instances...</p>
                      )}
                    </div>
                  </div>
                )}

                {incompleteInstances.length > 0 && (
                  <div className="mb-4">
                    <h6>Incomplete Instances to be Completed:</h6>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <Table striped size="sm">
                        <thead>
                          <tr>
                            <th>Instance ID</th>
                            <th>Student ID</th>
                            <th>Assessment ID</th>
                            <th>Date Assigned</th>
                            <th>Answer Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          {incompleteInstances.slice(0, 50).map(({ assessmentInstance, answerCount }) => (
                            <tr key={assessmentInstance.id}>
                              <td className="font-monospace small">{assessmentInstance.id}</td>
                              <td>{assessmentInstance.studentid}</td>
                              <td className="font-monospace small">{assessmentInstance.assessmentid}</td>
                              <td>{new Date(assessmentInstance.date).toLocaleDateString()}</td>
                              <td><Badge bg="info">{answerCount}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      {incompleteInstances.length > 50 && (
                        <p className="text-muted small">Showing first 50 of {incompleteInstances.length} instances...</p>
                      )}
                    </div>
                  </div>
                )}

                <div className="d-flex justify-content-between">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setCleanupStep('form')}
                  >
                    Back to Form
                  </Button>
                  <div>
                    <Button
                      variant="outline-secondary"
                      className="me-2"
                      onClick={handleCloseCleanupModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleExecuteCleanup}
                      disabled={loading || (cleanupResults.orphanedFound === 0 && cleanupResults.incompleteFound === 0)}
                    >
                      {loading ? <Spinner animation="border" size="sm" className="me-2" /> : <TrashFill className="me-2" />}
                      Execute Cleanup
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {cleanupStep === 'executing' && (
              <div className="text-center py-5">
                <Spinner animation="border" size="lg" />
                <h4 className="mt-3">Executing Cleanup...</h4>
                <p className="text-muted">Please wait while the cleanup process completes. Do not close this window.</p>
              </div>
            )}

            {cleanupStep === 'completed' && cleanupResults && (
              <div>
                <Alert variant="success">
                  <CheckCircle className="me-2" />
                  <strong>Cleanup Completed Successfully!</strong>
                </Alert>

                <Row className="mb-4">
                  <Col md={6}>
                    <Card className="border-success">
                      <Card.Header className="bg-success text-white">
                        <h6 className="mb-0">Orphaned Instances Deleted</h6>
                      </Card.Header>
                      <Card.Body>
                        <h4 className="text-success">{cleanupResults.orphanedDeleted}</h4>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="border-success">
                      <Card.Header className="bg-success text-white">
                        <h6 className="mb-0">Incomplete Instances Completed</h6>
                      </Card.Header>
                      <Card.Body>
                        <h4 className="text-success">{cleanupResults.incompleteCompleted}</h4>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end">
                  <Button
                    variant="primary"
                    onClick={handleCloseCleanupModal}
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </Modal.Body>
        </Modal>
      )}

      {/* Screening Window Management Modal - Staff Only */}
      {!teacherMode && (
        <Modal
          show={showScreeningWindowModal}
          onHide={handleCloseScreeningWindowModal}
          size="xl"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <Calendar className="me-2" />
              Screening Window Management
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Alert variant="info">
              <InfoCircleFill className="me-2" />
              <strong>Screening Windows:</strong> Define time periods when screening assessments are accessible.
              Windows can be set at country level or for specific schools.
            </Alert>

            {/* Create/Edit Form */}
            <Card className={`mb-4 ${editingWindow ? 'border-warning border-2' : ''}`}>
              <Card.Header className={editingWindow ? 'bg-warning' : 'bg-light'}>
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    {editingWindow ? (
                      <>
                        <Eye className="me-2" />
                        Editing Screening Window
                      </>
                    ) : (
                      'Create Screening Window'
                    )}
                  </h6>
                  {editingWindow && (
                    <Badge bg="dark">
                      Edit Mode: {availableCountries.find(c => c.id === editingWindow.countryid)?.name || editingWindow.countryid}
                    </Badge>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Country *</Form.Label>
                        <Form.Select
                          value={windowFormData.countryid}
                          onChange={(e) => handleWindowFormChange('countryid', e.target.value)}
                          required
                        >
                          <option value="">Select Country</option>
                          {availableCountries.map(country => (
                            <option key={country.id} value={country.id}>
                              {country.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Academic Year *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g., 2024/2025"
                          value={windowFormData.academic_year}
                          onChange={(e) => handleWindowFormChange('academic_year', e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Start Date *</Form.Label>
                        <Form.Control
                          type="date"
                          value={windowFormData.start_date}
                          onChange={(e) => handleWindowFormChange('start_date', e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>End Date *</Form.Label>
                        <Form.Control
                          type="date"
                          value={windowFormData.end_date}
                          onChange={(e) => handleWindowFormChange('end_date', e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Screening Window *</Form.Label>
                        <Form.Select
                          value={windowFormData.screening_window}
                          onChange={(e) => handleWindowFormChange('screening_window', e.target.value)}
                          required
                        >
                          <option value="Baseline">Baseline</option>
                          <option value="Midpoint">Midpoint</option>
                          <option value="End of Year">End of Year</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Status</Form.Label>
                        <Form.Check
                          type="switch"
                          id="active-switch"
                          label={windowFormData.active ? "Active" : "Inactive"}
                          checked={windowFormData.active}
                          onChange={(e) => handleWindowFormChange('active', e.target.checked)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Display Message (Optional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Message to display to users during this window"
                      value={windowFormData.message}
                      onChange={(e) => handleWindowFormChange('message', e.target.value)}
                    />
                  </Form.Group>

                  <div className="d-flex justify-content-end gap-2">
                    {editingWindow && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setEditingWindow(null);
                          resetWindowForm();
                        }}
                      >
                        Cancel Edit
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      onClick={handleSaveWindow}
                      disabled={loading}
                    >
                      {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                      {editingWindow ? 'Update Window' : 'Create Window'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {/* Existing Windows Table */}
            <Card>
              <Card.Header className="bg-light">
                <h6 className="mb-0">Existing Screening Windows</h6>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" />
                    <p className="mt-2">Loading screening windows...</p>
                  </div>
                ) : screeningWindows.length === 0 ? (
                  <Alert variant="info">
                    <InfoCircleFill className="me-2" />
                    No screening windows configured yet. Create one using the form above.
                  </Alert>
                ) : (
                  <Table striped hover responsive>
                    <thead>
                      <tr>
                        <th>Country</th>
                        <th>Academic Year</th>
                        <th>Window</th>
                        <th>Date Range</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {screeningWindows.map(window => (
                        <tr key={window.id}>
                          <td>{availableCountries.find(c => c.id === window.countryid)?.name || window.countryid}</td>
                          <td>{window.academic_year}</td>
                          <td>
                            <Badge bg="secondary">{window.screening_window}</Badge>
                          </td>
                          <td className="small">{formatDateRange(window.start_date, window.end_date)}</td>
                          <td>
                            <Badge bg={window.active ? 'success' : 'secondary'}>
                              {window.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleEditWindow(window)}
                              >
                                <Eye size={14} />
                              </Button>
                              <Button
                                size="sm"
                                variant={window.active ? 'outline-warning' : 'outline-success'}
                                onClick={() => handleToggleWindowActive(window.id, window.active)}
                              >
                                {window.active ? <EyeSlash size={14} /> : <Eye size={14} />}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleDeleteWindow(window.id)}
                              >
                                <TrashFill size={14} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Modal.Body>
        </Modal>
      )}

      {/* Class Screener Assignment View */}
      {showClassAssignmentView && (
        <Modal show={showClassAssignmentView} onHide={() => setShowClassAssignmentView(false)} size="xl" fullscreen>
          <Modal.Header closeButton>
            <Modal.Title>
              <PersonPlus className="me-2" />
              Assign Screeners to Classes - {schoolName}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            <ClassScreenerAssignment
              schoolId={schoolid}
              schoolName={schoolName}
              onClose={() => setShowClassAssignmentView(false)}
            />
          </Modal.Body>
        </Modal>
      )}

      {/* Class Screener Removal View */}
      {showClassRemovalView && (
        <Modal show={showClassRemovalView} onHide={() => setShowClassRemovalView(false)} size="xl" fullscreen>
          <Modal.Header closeButton>
            <Modal.Title>
              <PersonDash className="me-2" />
              Remove Screeners from Classes - {schoolName}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            <ClassScreenerRemoval
              schoolId={schoolid}
              schoolName={schoolName}
              onClose={() => setShowClassRemovalView(false)}
            />
          </Modal.Body>
        </Modal>
      )}

      {/* Standardised Score Controls Modal - Staff Only */}
      {!teacherMode && (
        <CalculateStandardisedScoresModal
          show={showCalculateStandardisedScoresModal}
          onHide={() => setShowCalculateStandardisedScoresModal(false)}
        />
      )}

      {/* Step-by-step Standardised Score Controls Modal - Staff Only, additive alongside the modal above */}
      {!teacherMode && (
        <CalculateStandardisedScoresStepModal
          show={showCalculateStandardisedScoresStepModal}
          onHide={() => setShowCalculateStandardisedScoresStepModal(false)}
        />
      )}

      {!teacherMode && (
        <Modal
          show={showStandardisedScoreModal}
          onHide={handleCloseStandardisedScoreModal}
          size="xl"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <CheckCircle className="me-2" />
              Standardised Score Controls
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Alert variant="info">
              <InfoCircleFill className="me-2" />
              <strong>Standardised Scores:</strong> Control when standardised scores are available for each screening assessment.
              Standardised scores require sufficient data collection before being enabled.
            </Alert>

            {loadingStandardisedScores ? (
              <div className="text-center py-5">
                <Spinner animation="border" size="lg" />
                <p className="mt-3">Loading assessments...</p>
              </div>
            ) : Object.keys(assessmentsWithTags).length === 0 ? (
              <Alert variant="warning">
                <ExclamationTriangleFill className="me-2" />
                No assessments found or standardised_scores_enabled tag definition not configured.
              </Alert>
            ) : (
              <>
                {Object.entries(assessmentsWithTags).map(([window, assessments]) => (
                  <Card key={window} className="mb-4">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">
                        <Calendar className="me-2" />
                        {window}
                      </h6>
                    </Card.Header>
                    <Card.Body>
                      <Table striped hover responsive>
                        <thead>
                          <tr>
                            <th>Assessment Name</th>
                            <th>Assessment ID</th>
                            <th className="text-center">Calculation Completeness</th>
                            <th className="text-center">Standardised Scores</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assessments.map(assessment => (
                            <tr key={assessment.id}>
                              <td>{assessment.name}</td>
                              <td className="font-monospace small text-muted">
                                {assessment.id}
                              </td>
                              <td className="text-center">
                                {assessment.totalTaskCount === null ? (
                                  <span className="text-muted small">Unknown</span>
                                ) : (
                                  <Badge
                                    bg={
                                      assessment.totalTaskCount > 0 &&
                                      assessment.calculatedTaskCount === assessment.totalTaskCount
                                        ? 'success'
                                        : 'secondary'
                                    }
                                  >
                                    {assessment.calculatedTaskCount} / {assessment.totalTaskCount} tasks calculated
                                  </Badge>
                                )}
                              </td>
                              <td className="text-center">
                                <Badge
                                  bg={assessment.standardisedScoresEnabled ? 'success' : 'secondary'}
                                  className="px-3 py-2"
                                >
                                  {assessment.standardisedScoresEnabled ? 'ON' : 'OFF'}
                                </Badge>
                              </td>
                              <td className="text-center">
                                <Form.Check
                                  type="switch"
                                  id={`switch-${assessment.id}`}
                                  checked={assessment.standardisedScoresEnabled}
                                  onChange={() => handleToggleStandardisedScore(
                                    assessment.id,
                                    assessment.standardisedScoresEnabled,
                                    assessment
                                  )}
                                  disabled={loadingStandardisedScores}
                                  label=""
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                ))}
              </>
            )}

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="secondary"
                onClick={handleCloseStandardisedScoreModal}
              >
                Close
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      )}

      {/* Country Standardised Score Controls Modal - Staff Only */}
      {!teacherMode && (
        <Modal
          show={showCountryStandardisedScoreModal}
          onHide={handleCloseCountryStandardisedScoreModal}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <Shield className="me-2" />
              Country Standardised Score Controls
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Alert variant="info">
              <InfoCircleFill className="me-2" />
              <strong>Country Controls:</strong> Enable or disable standardised scores at the country level.
              When disabled for a country, standardised scores will not be available regardless of assessment-level settings.
            </Alert>

            {error && (
              <Alert variant="danger" className="mb-3">
                <ExclamationTriangleFill className="me-2" />
                {error}
              </Alert>
            )}

            {success && (
              <Alert variant="success" className="mb-3">
                <CheckCircle className="me-2" />
                {success}
              </Alert>
            )}

            {loadingCountryStandardisedScores ? (
              <div className="text-center py-5">
                <Spinner animation="border" size="lg" />
                <p className="mt-3">Loading countries...</p>
              </div>
            ) : countriesWithTags.length === 0 ? (
              <Alert variant="warning">
                <ExclamationTriangleFill className="me-2" />
                No countries found or tag definition not configured.
              </Alert>
            ) : (
              <Card>
                <Card.Body>
                  <Table striped hover responsive>
                    <thead>
                      <tr>
                        <th>Country Name</th>
                        <th>Country ID</th>
                        <th className="text-center">Standardised Scores</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {countriesWithTags.map(country => (
                        <tr key={country.id}>
                          <td>{country.name}</td>
                          <td className="font-monospace small text-muted">
                            {country.id}
                          </td>
                          <td className="text-center">
                            <Badge
                              bg={country.standardisedScoresEnabled ? 'success' : 'secondary'}
                              className="px-3 py-2"
                            >
                              {country.standardisedScoresEnabled ? 'ON' : 'OFF'}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="switch"
                              id={`country-switch-${country.id}`}
                              checked={country.standardisedScoresEnabled}
                              onChange={() => handleToggleCountryStandardisedScore(
                                country.id,
                                country.standardisedScoresEnabled
                              )}
                              disabled={loadingCountryStandardisedScores}
                              label=""
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            <div className="d-flex justify-content-end mt-4">
              <Button
                variant="secondary"
                onClick={handleCloseCountryStandardisedScoreModal}
              >
                Close
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      )}

      {/* Bulk Assign Screeners Modal - Staff Only */}
      {!teacherMode && (
        <Modal
          show={showBulkAssignModal}
          onHide={handleCloseBulkAssignModal}
          size="xl"
          backdrop="static"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <LightningFill className="me-2" />
              Bulk Assign Screeners
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Alert variant="warning">
              <ExclamationTriangleFill className="me-2" />
              <strong>Warning:</strong> This will assign screeners to all eligible students in the selected country. Always preview before executing.
            </Alert>

            {/* FORM STEP */}
            {(bulkAssignStep === 'form' || bulkAssignStep === 'previewing') && (
              <div>
                <Form>
                  <Row className="mb-4">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Country <span className="text-danger">*</span></Form.Label>
                        <Form.Select
                          value={bulkAssignCountry}
                          onChange={(e) => {
                            setBulkAssignCountry(e.target.value);
                            setBulkAssignSelectedAssessments(new Set());
                          }}
                        >
                          <option value="">Select a country...</option>
                          {availableCountries.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  {bulkAssignCountry && (
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6>Select Assessments to Assign</h6>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            const visible = getBulkFilteredAssessments();
                            if (bulkAssignSelectedAssessments.size === visible.length) {
                              setBulkAssignSelectedAssessments(new Set());
                            } else {
                              setBulkAssignSelectedAssessments(new Set(visible.map(a => a.id)));
                            }
                          }}
                        >
                          {bulkAssignSelectedAssessments.size === getBulkFilteredAssessments().length ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>

                      {loading ? (
                        <div className="text-center py-4">
                          <Spinner animation="border" />
                          <p className="mt-2">Loading assessments...</p>
                        </div>
                      ) : (() => {
                        const filtered = getBulkFilteredAssessments();
                        const standardAssessments = filtered.filter(a => a.standard === true);
                        const nonStandardAssessments = filtered.filter(a => a.standard !== true);
                        const sortByPeriod = (arr) => [...arr].sort((a, b) => getPeriodOrder(a.name) - getPeriodOrder(b.name));
                        const grade1 = sortByPeriod(standardAssessments.filter(a => a.grade_level1 === true));
                        const grade2 = sortByPeriod(standardAssessments.filter(a => a.grade_level2 === true));

                        const renderCheckboxes = (assessments) => (
                          <div className="d-flex flex-wrap gap-2">
                            {assessments.map(a => (
                              <Form.Check
                                key={a.id}
                                type="checkbox"
                                id={`bulk-assessment-${a.id}`}
                                label={a.name}
                                checked={bulkAssignSelectedAssessments.has(a.id)}
                                onChange={() => handleBulkAssignToggle(a.id)}
                                className="border rounded p-2"
                              />
                            ))}
                          </div>
                        );

                        return (
                          <>
                            {grade1.length > 0 && (
                              <div className="mb-3">
                                <h6 className="text-muted mb-2">Grade 1</h6>
                                {renderCheckboxes(grade1)}
                              </div>
                            )}
                            {grade2.length > 0 && (
                              <div className="mb-3">
                                <h6 className="text-muted mb-2">Grade 2</h6>
                                {renderCheckboxes(grade2)}
                              </div>
                            )}
                            {nonStandardAssessments.length > 0 && (
                              <div className="mb-3">
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-muted mb-2"
                                  onClick={() => setBulkAssignShowNonStandard(prev => !prev)}
                                >
                                  {bulkAssignShowNonStandard ? '▾' : '▸'} Non-standard ({nonStandardAssessments.length})
                                </Button>
                                {bulkAssignShowNonStandard && renderCheckboxes(sortByPeriod(nonStandardAssessments))}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}

                  <div className="d-flex justify-content-between">
                    <Button variant="outline-secondary" onClick={handleCloseBulkAssignModal}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleBulkAssignPreview}
                      disabled={bulkAssignStep === 'previewing' || !bulkAssignCountry || bulkAssignSelectedAssessments.size === 0}
                    >
                      {bulkAssignStep === 'previewing'
                        ? <><Spinner animation="border" size="sm" className="me-2" />Previewing...</>
                        : <><Eye className="me-2" />Preview Assignment</>
                      }
                    </Button>
                  </div>
                </Form>
              </div>
            )}

            {/* PREVIEW STEP */}
            {bulkAssignStep === 'preview' && (
              <div>
                <Alert variant="info">
                  <InfoCircleFill className="me-2" />
                  <strong>Preview:</strong> The following assignments will be made to all eligible students in <strong>{availableCountries.find(c => c.id === bulkAssignCountry)?.name ?? bulkAssignCountry}</strong>. No changes have been made yet.
                </Alert>

                <Table bordered hover size="sm" className="mb-4">
                  <thead>
                    <tr>
                      <th>Assessment</th>
                      <th className="text-center">Schools</th>
                      <th className="text-center">Students to Assign</th>
                      <th className="text-center">Skipped (Active)</th>
                      <th className="text-center">Skipped (Completed)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkAssignPreviewResults.map(r => (
                      <tr key={r.assessmentId}>
                        <td>{r.assessmentName}</td>
                        <td className="text-center">{r.schoolsProcessed}</td>
                        <td className="text-center text-success fw-bold">{r.studentsAssigned}</td>
                        <td className="text-center text-muted">{r.studentsSkippedActive}</td>
                        <td className="text-center text-muted">{r.studentsSkippedCompleted}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <div className="d-flex justify-content-between">
                  <Button variant="outline-secondary" onClick={() => setBulkAssignStep('form')}>
                    Back
                  </Button>
                  <div>
                    <Button variant="outline-secondary" className="me-2" onClick={handleCloseBulkAssignModal}>
                      Cancel
                    </Button>
                    <Button
                      variant="success"
                      onClick={handleBulkAssignExecute}
                      disabled={bulkAssignPreviewResults.every(r => r.studentsAssigned === 0)}
                    >
                      <LightningFill className="me-2" />
                      Execute Bulk Assignment
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* EXECUTING STEP */}
            {bulkAssignStep === 'executing' && (
              <div>
                <Alert variant="info">
                  <InfoCircleFill className="me-2" />
                  Assigning screeners sequentially. Do not close this window.
                </Alert>
                <Table bordered size="sm">
                  <thead>
                    <tr>
                      <th>Assessment</th>
                      <th className="text-center">Status</th>
                      <th className="text-center">Schools</th>
                      <th className="text-center">Assigned</th>
                      <th className="text-center">Skipped (Active)</th>
                      <th className="text-center">Skipped (Completed)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkAssignResults.map(r => (
                      <tr key={r.assessmentId}>
                        <td>{r.assessmentName}</td>
                        <td className="text-center">
                          {r.status === 'pending' && <span className="text-muted">Waiting</span>}
                          {r.status === 'executing' && <Spinner animation="border" size="sm" />}
                          {r.status === 'done' && <Badge bg="success">Done</Badge>}
                          {r.status === 'error' && <Badge bg="danger">Error</Badge>}
                        </td>
                        <td className="text-center">{r.status === 'done' ? r.schoolsProcessed : '—'}</td>
                        <td className="text-center">{r.status === 'done' ? r.studentsAssigned : '—'}</td>
                        <td className="text-center">{r.status === 'done' ? r.studentsSkippedActive : '—'}</td>
                        <td className="text-center">{r.status === 'done' ? r.studentsSkippedCompleted : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {/* COMPLETED STEP */}
            {bulkAssignStep === 'completed' && (
              <div>
                <Alert variant="success">
                  <CheckCircle className="me-2" />
                  <strong>Bulk assignment completed.</strong>
                </Alert>
                <Table bordered size="sm" className="mb-4">
                  <thead>
                    <tr>
                      <th>Assessment</th>
                      <th className="text-center">Status</th>
                      <th className="text-center">Schools Processed</th>
                      <th className="text-center">Students Assigned</th>
                      <th className="text-center">Skipped (Active)</th>
                      <th className="text-center">Skipped (Completed)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkAssignResults.map(r => (
                      <tr key={r.assessmentId}>
                        <td>{r.assessmentName}</td>
                        <td className="text-center">
                          {r.status === 'done' && <Badge bg="success">Done</Badge>}
                          {r.status === 'error' && <Badge bg="danger" title={r.errorMessage}>Error</Badge>}
                        </td>
                        <td className="text-center">{r.schoolsProcessed ?? '—'}</td>
                        <td className="text-center">{r.studentsAssigned ?? '—'}</td>
                        <td className="text-center">{r.studentsSkippedActive ?? '—'}</td>
                        <td className="text-center">{r.studentsSkippedCompleted ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="d-flex justify-content-end">
                  <Button variant="primary" onClick={handleCloseBulkAssignModal}>Close</Button>
                </div>
              </div>
            )}
          </Modal.Body>
        </Modal>
      )}

      {isPortrait && <OrientationPrompt />}
    </div>
  );
}

export default ScreenerManagement;
